/**
 * src/components/map/mapSurfaces.ts
 *
 * Which map surfaces exist, which one yields, and to what.
 *
 * Until now nothing knew. The country hover card renders from `App`
 * and the spatial cards render from `SpatialObjectsMapLayer` — sibling
 * subtrees, both absolutely positioned over the same map container,
 * both at `zIndex: 10000`, neither aware the other exists. Hovering a
 * country while a conversation is open put one card on top of the
 * other, and the only tool either had for getting out of the way was a
 * hardcoded rectangle describing the nav buttons.
 *
 * The fix is not a bigger z-index or a better guessed offset. It is
 * that a surface declares *what it is* and the coordinator decides
 * where it goes. Three ideas carry that:
 *
 *   **Deliberate beats incidental.** A conversation, a payment form and
 *   a player were opened on purpose. A hover card appeared because a
 *   pointer moved across a shape. A hover card must never push a
 *   conversation around, and the ladder below is that sentence written
 *   as data.
 *
 *   **Protected surfaces are never covered.** Navigation and a
 *   transaction confirmation are avoided by everything, at every rank.
 *   A card that hides the button you need to dismiss it is a trap, and
 *   a card over a signing prompt is worse than a trap.
 *
 *   **Nothing here owns a coordinate.** This module ranks and filters
 *   rectangles that surfaces measured about themselves. It never
 *   decides that a card belongs 20 px right of the cursor — that is
 *   `placeMapTooltip`'s arithmetic, and keeping the two apart is what
 *   lets a feature declare map intent without owning screen position.
 *
 * Pure and dependency-free, so every rule is testable without a map or
 * a React tree. The store in `mapOverlayStore` is only the plumbing
 * that collects what surfaces report; the policy is all here.
 */

import type { Rect } from './mapTooltipPlacement';

/**
 * Every surface that can be open over the map.
 *
 * Named for what it is rather than for the component that renders it,
 * because two components have rendered the same kind of thing before
 * and will again.
 */
export type MapSurfaceKind =
  // Incidental: appeared because a pointer moved.
  | 'country_tooltip'
  | 'object_tooltip'
  // Deliberate: opened by a click.
  | 'search_results'
  | 'object_listen'
  | 'object_deposit'
  | 'object_chat'
  | 'event_panel'
  | 'rule_manager'
  | 'inspector'
  | 'country_panel'
  // Protected: avoided by everything.
  | 'nav_controls'
  | 'transaction_confirm';

/**
 * The ladder, least important first.
 *
 * Order *is* the priority — there are no numbers to drift out of step
 * with each other, and inserting a surface is a matter of putting it
 * in the right place in a list a person can read.
 */
export const SURFACE_ORDER: readonly MapSurfaceKind[] = Object.freeze([
  'country_tooltip',
  'object_tooltip',
  'search_results',
  'object_listen',
  'object_deposit',
  'object_chat',
  'event_panel',
  'rule_manager',
  'inspector',
  'country_panel',
  'nav_controls',
  'transaction_confirm',
]);

/**
 * Surfaces that occupy the visitor's attention rather than decorating
 * the map.
 *
 * On a narrow viewport only one of these may be open at a time: two
 * stacked panels on a phone leaves neither usable. Hover cards are
 * excluded because they are not a destination, and protected furniture
 * is excluded because it is not something a visitor opens.
 */
const MAJOR: ReadonlySet<MapSurfaceKind> = new Set<MapSurfaceKind>([
  'search_results',
  'object_listen',
  'object_deposit',
  'object_chat',
  'event_panel',
  'rule_manager',
  'inspector',
  'country_panel',
]);

/**
 * Surfaces nothing may cover, at any rank.
 *
 * Navigation, because a card over the menu is a card you cannot
 * escape. A transaction confirmation, because obscuring what somebody
 * is about to sign is the most expensive thing this file could allow.
 */
const PROTECTED: ReadonlySet<MapSurfaceKind> = new Set<MapSurfaceKind>([
  'nav_controls',
  'transaction_confirm',
]);

/**
 * Where "one major surface at a time" starts applying.
 *
 * The same breakpoint `App` uses for its own mobile map handling
 * (`isMobileViewport`, `window.innerWidth <= 768`). Stated here so the
 * overlay rules and the map's own idea of a phone cannot drift apart,
 * and so the number has a reason attached to it somewhere.
 */
export const NARROW_VIEWPORT_MAX_PX = 768;

export function isNarrowViewport(width: number): boolean {
  return width > 0 && width <= NARROW_VIEWPORT_MAX_PX;
}

/** One open surface, as the store holds it. */
export interface MapSurface {
  /** Unique per open surface; two chats about two objects are two ids. */
  id: string;
  kind: MapSurfaceKind;
  /**
   * Where it actually is, in map-container pixels, once it knows.
   *
   * Null until measured. An unmeasured surface is avoided by nothing,
   * which is the right default: inventing a rectangle for it would
   * move other cards away from a place it might not be.
   */
  rect: Rect | null;
}

/**
 * Rank of a surface kind. Higher yields less.
 *
 * An unknown kind ranks below everything rather than above it. A
 * surface nobody has classified should get out of the way of the ones
 * that were, not inherit authority it was never granted.
 */
export function surfacePriority(kind: MapSurfaceKind | string): number {
  return SURFACE_ORDER.indexOf(kind as MapSurfaceKind);
}

export function isMajorSurface(kind: MapSurfaceKind | string): boolean {
  return MAJOR.has(kind as MapSurfaceKind);
}

/**
 * Coordinated cards stack from here, by rank.
 *
 * Chosen so the whole ladder lands below 10000, where this
 * application's dialogs and overlays live: a dialog should cover a
 * hover card, not lose to one.
 */
export const SURFACE_Z_BASE = 9_999 - SURFACE_ORDER.length;

/**
 * The stacking order for a surface, as a number CSS will take.
 *
 * Repositioning is the primary mechanism and this is the fallback for
 * a viewport too small for anything to clear — but it is also the only
 * thing standing between a hover card and the conversation underneath
 * it, which is why it is derived from the ladder rather than chosen.
 *
 * It lives here rather than in the shell that renders most cards
 * because not every surface is that shell. The mobile country card is
 * its own markup and picked `9999` by hand, which put an incidental
 * card above every deliberate one — the exact inversion `SURFACE_ORDER`
 * exists to prevent. A surface that cannot use the shell can still use
 * the ladder.
 *
 * An unknown kind lands at the bottom, for the same reason
 * `surfacePriority` ranks it there.
 */
export function surfaceZIndex(kind: MapSurfaceKind | string): number {
  return SURFACE_Z_BASE + Math.max(0, surfacePriority(kind));
}

export function isProtectedSurface(kind: MapSurfaceKind | string): boolean {
  return PROTECTED.has(kind as MapSurfaceKind);
}

/**
 * Whether `self` should move out of `other`'s way.
 *
 * Protected surfaces are yielded to unconditionally. Otherwise the
 * higher rank wins, and an exact tie is broken on id so that one of
 * the two moves and the other stays put. Without the tiebreak, two
 * equal surfaces would each wait for the other and both would stay
 * where they were.
 */
export function shouldYieldTo(
  self: { id: string; kind: MapSurfaceKind | string },
  other: { id: string; kind: MapSurfaceKind | string },
): boolean {
  if (self.id === other.id) return false;
  if (isProtectedSurface(other.kind)) return true;
  if (isProtectedSurface(self.kind)) return false;

  const mine = surfacePriority(self.kind);
  const theirs = surfacePriority(other.kind);
  if (theirs !== mine) return theirs > mine;
  return other.id < self.id;
}

/**
 * The rectangles a surface must keep clear of.
 *
 * Everything it outranks is absent from the result, which is what lets
 * a conversation stay where it was opened while a hover card moves.
 * Surfaces that have not measured themselves contribute nothing.
 *
 * `furniture` is passed in rather than computed here: the permanent
 * control clusters are the host application's furniture, and this
 * module has no business knowing where that application puts its
 * buttons. Callers that have registered their controls as surfaces can
 * pass nothing at all.
 */
export function avoidZonesFor(input: {
  surfaces: readonly MapSurface[];
  self: { id: string; kind: MapSurfaceKind | string };
  furniture?: readonly Rect[];
}): Rect[] {
  const { surfaces, self } = input;
  const zones: Rect[] = [...(input.furniture ?? [])];

  for (const surface of surfaces) {
    if (!surface.rect) continue;
    if (!shouldYieldTo(self, surface)) continue;
    zones.push(surface.rect);
  }

  return zones;
}

/**
 * The major surface already open, if there is one.
 *
 * For the narrow-viewport rule. Returns the most important one when
 * several are somehow open, because that is the one worth keeping.
 */
export function openMajorSurface(
  surfaces: readonly MapSurface[],
  exceptId?: string,
): MapSurface | null {
  let winner: MapSurface | null = null;
  for (const surface of surfaces) {
    if (surface.id === exceptId) continue;
    if (!isMajorSurface(surface.kind)) continue;
    if (!winner || surfacePriority(surface.kind) > surfacePriority(winner.kind)) {
      winner = surface;
    }
  }
  return winner;
}
